// Squad mounting (GDD §27): squads riding host squads (Crown Colossus battlements,
// Groveback howdahs, War Engine modules). Mount/dismount commands live in sim.ts;
// this system slaves rider positions to their hosts each tick after movement.

import { buildingDef, unitDef } from '../../content/registry';
import type { Sim } from '../sim';
import type { Building, EntId, Squad } from '../state';
import { footprintOf } from '../state';

const MOUNT_SOURCE = 'mount-effect';
/** Castle walls always carry at most two squads, even if content data is misconfigured. */
export const MAX_WALL_MOUNTED_SQUADS = 2;

export function wallMountCapacity(contentSlots: number | undefined): number {
  return Math.min(MAX_WALL_MOUNTED_SQUADS, Math.max(0, Math.floor(contentSlots ?? 0)));
}

/**
 * Stable battlement slot positions. Slots follow the wall's long axis, which matters for
 * the horizontal segments around each base (the old fixed Y offset put riders beside them).
 */
export function wallMountSlotPosition(wall: Building, slot: number): { x: number; y: number } {
  const fp = footprintOf(buildingDef(wall.defId), wall.rot);
  const long = Math.max(fp.w, fp.h);
  const gateSpan = Math.min(wall.gateSpan, Math.max(0, long - 2));
  // Center each rider on one of the two solid flank battlements. With the 8 m giant-safe
  // aperture in a 12 m wall this yields ±5 m, never floating a garrison over the doorway.
  const flankCenter = Math.min(long / 2 - 0.5, (long + gateSpan) / 4);
  const offset = slot === 0 ? -flankCenter : flankCenter;
  return fp.w >= fp.h
    ? { x: wall.pos.x + offset, y: wall.pos.y }
    : { x: wall.pos.x, y: wall.pos.y + offset };
}

/** Drop a wall garrison on the protected/home side of its wall. */
export function dismountWallSquad(sim: Sim, squad: Squad): boolean {
  if (squad.mountedOnWall === null) return false;
  const wall = sim.state.buildings.get(squad.mountedOnWall);
  squad.mountedOnWall = null;

  if (wall) {
    const fp = footprintOf(buildingDef(wall.defId), wall.rot);
    const core = sim.map.corePositions[squad.owner];
    const dx = core.x - wall.pos.x;
    const dy = core.y - wall.pos.y;
    // Exit along the wall's thin/normal axis, toward home. Choosing the axis from the
    // core vector could send a horizontal segment along its long edge into a neighbor.
    const verticalWall = fp.h >= fp.w;
    const exit = verticalWall
      ? {
          x: wall.pos.x + (dx < 0 ? -1 : 1) * (fp.w / 2 + 2.5),
          y: wall.pos.y,
        }
      : {
          x: wall.pos.x,
          y: wall.pos.y + (dy < 0 ? -1 : 1) * (fp.h / 2 + 2.5),
        };
    squad.pos = sim.nav.nearestWalkable(exit, 12, squad.owner);
  } else {
    // Defensive fallback for a stale snapshot or a wall removed outside destroyBuilding().
    squad.pos = sim.nav.nearestWalkable(squad.pos, 12, squad.owner);
  }
  squad.prevPos = { ...squad.pos };
  squad.lastOpenPos = { ...squad.pos };
  squad.path = null;
  squad.pathGoal = null;
  squad.moveTarget = null;
  squad.targetId = null;
  squad.engaged = false;
  squad.stuckTime = 0;
  squad.progressGoal = null;
  squad.bestRouteRemaining = Infinity;
  squad.progressNavRevision = sim.nav.revision;
  squad.progressRouteSignature = 0;
  squad.breachResume = null;
  return true;
}

export function stepMounting(sim: Sim): void {
  const st = sim.state;
  // strip last tick's mount effects (reapplied below; never stacks)
  for (const s of st.squads.values()) {
    for (let i = s.buffs.length - 1; i >= 0; i--) {
      if ((s.buffs[i] as { source?: string }).source === MOUNT_SOURCE) s.buffs.splice(i, 1);
    }
  }
  // Enforce wall occupancy as a simulation invariant. This also repairs stale/legacy
  // snapshots deterministically: the two lowest squad ids keep the available slots.
  const wallRiders = new Map<EntId, Squad[]>();
  for (const s of st.squads.values()) {
    if (s.mountedOnWall === null) continue;
    const list = wallRiders.get(s.mountedOnWall);
    if (list) list.push(s);
    else wallRiders.set(s.mountedOnWall, [s]);
  }
  for (const [wallId, list] of wallRiders) {
    const wall = st.buildings.get(wallId);
    const capacity = wall && wall.state === 'active' ? wallMountCapacity(buildingDef(wall.defId).mountSlots) : 0;
    list.sort((a, b) => a.id - b.id);
    for (let i = 0; i < list.length; i++) {
      const squad = list[i];
      if (!wall || squad.owner !== wall.owner || i >= capacity) {
        dismountWallSquad(sim, squad);
        squad.command = { kind: 'defend', pointA: { ...squad.pos }, auto: true };
        continue;
      }
      squad.pos = wallMountSlotPosition(wall, i);
      squad.prevPos = { ...squad.pos };
    }
  }

  // gather riders per host (stable order: ascending rider id → deterministic slots)
  let riders: Map<EntId, Squad[]> | null = null;
  for (const s of st.squads.values()) {
    if (s.mountedOnSquad === null) continue;
    riders ??= new Map();
    const list = riders.get(s.mountedOnSquad);
    if (list) list.push(s);
    else riders.set(s.mountedOnSquad, [s]);
  }
  if (!riders) return;
  for (const [hostId, list] of riders) {
    const host = st.squads.get(hostId);
    if (!host) {
      // host vanished outside killSquad (shouldn't happen, but never strand a rider)
      for (const s of list) {
        s.mountedOnSquad = null;
        s.pos = sim.nav.nearestWalkable(s.pos, 12, s.owner);
        s.prevPos = { ...s.pos };
        s.lastOpenPos = { ...s.pos };
        s.stuckTime = 0;
        s.progressGoal = null;
        s.bestRouteRemaining = Infinity;
        s.progressNavRevision = sim.nav.revision;
        s.progressRouteSignature = 0;
        s.breachResume = null;
      }
      continue;
    }
    list.sort((a, b) => a.id - b.id);
    for (let i = 0; i < list.length; i++) {
      const s = list[i];
      // perch offsets around the host's shoulders; render sorts out the visual stacking
      const side = i % 2 === 0 ? -1 : 1;
      const off = 0.9 + Math.floor(i / 2) * 0.7;
      s.pos = { x: host.pos.x + side * off, y: host.pos.y - 0.5 };
      s.prevPos = { ...s.pos };
      s.facing = host.facing;
      // rider passives flow to the host (shield screens)
      const me = unitDef(s.defId).mountEffect;
      if (me) {
        host.buffs.push(
          Object.assign({ stat: me.stat as never, mult: me.mult, remaining: 0.11 }, { source: MOUNT_SOURCE }),
        );
      }
    }
  }
}
